import React, { useMemo, useState } from "react";
import styled from "styled-components";
import MyInfoBannerImage from "../../assets/image/myinfo_banner_curriculum_square.png";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DOCS_BASE_URL } from "../../util/constant";

const breakpoints = {
  tablet: 768,
  phone: 480,
};

const DocumentsBlock = styled.div`
  display: flex;
  flex-direction: column;
  /* remove top padding so banner aligns with right banner */
  padding: 0 10px 10px;
  max-width: 100%;

  @media (max-width: ${breakpoints.tablet}px) {
    max-width: 30%;
  }
  @media (max-width: ${breakpoints.phone}px) {
    align-items: center;
    padding: 0 10px;
    max-width: 80%;
    margin: 0 auto;
  }
`;

const BannerLink = styled.a`
  display: block;
`;

// desktop/tablet banner
const BannerImage = styled.img`
  width: 100%;
  /* match right-side banner margins for alignment */
  margin: 10px 0;
  display: block;

  @media (max-width: ${breakpoints.phone}px) {
    display: none;
  }

  /* align with CurriculumBanner on tablets */
  @media (max-width: ${breakpoints.tablet}px) and (min-width: ${breakpoints.phone +
    1}px) {
    margin: 14px 0;
  }
`;

// banner inside the sidebar on phones
const SidebarBanner = styled.img`
  display: none;

  @media (max-width: ${breakpoints.phone}px) {
    display: block;
    width: 90%;
    max-width: 320px;
    margin: 12px auto;
  }
`;

const BurgerButton = styled.button`
  display: none;

  @media (max-width: ${breakpoints.phone}px) {
    position: absolute;
    margin-top: -206px;
    left: 10px;
    display: block;
    background: none;
    border: none;
    color: white;
    font-size: 2rem;
    cursor: pointer;
    z-index: 12;
  }
`;

const Overlay = styled.div`
  display: none;

  @media (max-width: ${breakpoints.phone}px) {
    display: ${({ open }) => (open ? "block" : "none")};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 10;
  }
`;

const Sidebar = styled.div`
  display: none;

  @media (max-width: ${breakpoints.phone}px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 80%;
    max-width: 300px;
    background: white;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.2);
    transform: ${({ open }) => (open ? "translateX(0)" : "translateX(-100%)")};
    transition: transform 0.25s ease;
    z-index: 11;
    padding-top: 2rem;
    gap: 10px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 12px;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
`;

const DocumentsMenu = styled.div`
  order: 0;
  width: 100%;

  @media (max-width: ${breakpoints.phone}px) {
    display: none;
  }
`;

const SidebarMenu = styled.div`
  padding: 1rem;
`;

const SectionHeader = styled.div`
  font-size: 1rem;
  font-weight: bold;
  padding: 10px;
  border-radius: 5px 5px 0 0;
  color: white;
  background-color: ${({ colors }) => colors.componentHeaderBackground};

  @media (max-width: ${breakpoints.phone}px) {
    font-size: 0.9rem;
    padding: 8px;
    margin-top: 10px;
  }
`;

const ResourceList = styled.ul`
  list-style: none;
  margin: 0;
  background-color: ${({ colors }) => colors.cardBackground};
  border-radius: 0 0 5px 5px;
  padding: 10px;

  @media (max-width: ${breakpoints.phone}px) {
    padding: 8px;
  }
`;

const ResourceItem = styled.li`
  margin-bottom: 8px;

  a {
    font-size: 0.85rem;
    color: #333;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  span {
    font-weight: bold;
    color: red;
  }
`;

// Standalone section hidden on phones
const StudentResourcesSection = styled.div`
  order: 2;
  margin-top: 20px;

  @media (max-width: ${breakpoints.phone}px) {
    display: none;
  }
`;

const StudentResourcesBody = styled.div`
  background-color: ${({ colors }) => colors.cardBackground};
  padding: 15px;
  border-radius: 0 0 5px 5px;
  font-size: 0.85rem;
  color: ${({ colors }) => colors.footerText};

  @media (max-width: ${breakpoints.phone}px) {
    padding: 10px;
  }
`;

export default function DashboardResources({ colors, curriculumLink }) {
  const resolvedCurriculumLink = useMemo(
    () => curriculumLink ?? null,
    [curriculumLink],
  );
  const navigate = useNavigate();
  const resources = useMemo(
    () => [
      {
        title: "Curriculum Library",
        url: resolvedCurriculumLink,
        isExternal: true,
        disabled: !curriculumLink,
      },
      {
        title: "Curriculum Download Process",
        url: `${DOCS_BASE_URL}/ISAFE-Curriculum-Download-Process.pdf`,
      },
      {
        title: "Common Core State Standards",
        url: "/myinfo/ccss",
        isInternal: true,
      },
      {
        title: "SCORM Info for e-Learning",
        url: `${DOCS_BASE_URL}/faq/2024-11/SCORMArticleTheDigitalClassroom_updated.pdf`,
      },
      {
        title: "Implementation Strategy",
        url: `${DOCS_BASE_URL}/Implementation_Strategy-2025-2026.pdf`,
      },
      {
        title: "Schedule A",
        url: `${DOCS_BASE_URL}/ISAFE_Schedule_A_2025-2026_updated.pdf`,
      },
      {
        title: "Curriculum Scope",
        url: `${DOCS_BASE_URL}/Curriculum_Scope_2025-2026.pdf`,
      },
      {
        title: "Category Descriptions",
        url: `${DOCS_BASE_URL}/ISAFE-Category-Descriptions_2025-2026.pdf`,
      },
      {
        title: "Digital Learning Platform",
        url: `${DOCS_BASE_URL}/ISAFE-Digital-Learning-Platform_2025-2026.pdf`,
      },
    ],
    [curriculumLink],
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const ISAFE_ROLES = new Set([
    "isafe_accountant",
    "isafe_moderator",
    "isafe_salesperson",
    "isafe_admin",
    "root",
  ]);
  const SUBSCRIPTION_ROLES = new Set([
    "subscription_access",
    "subscription_accountant",
    "subscription_admin",
    "subscription_implementor",
    "subscription_salesperson",
  ]);
  const become = useSelector((state) => state.auth.become);
  const become_user = useSelector((state) => state.auth.become_user);
  const roles = useSelector((state) => state.auth.user.roles);
  const roleIDs = new Set(roles?.map((r) => r.id));
  const hasSubscriptionAdminRole = Array.from(SUBSCRIPTION_ROLES).some((r) =>
    roleIDs.has(r),
  );
  const hasISAFEAdminRole = Array.from(ISAFE_ROLES).some((r) => roleIDs.has(r));

  const more_top =
    (become == 1 && hasISAFEAdminRole) ||
    (become == 0 && hasSubscriptionAdminRole && !hasISAFEAdminRole);

  console.log(more_top);
  return (
    <DocumentsBlock>
      {/* Desktop/tablet banner */}
      <BannerLink
        href={resolvedCurriculumLink}
        target="_blank"
        rel="noopener noreferrer"
      >
        <BannerImage src={MyInfoBannerImage} alt="Curriculum Banner" />
      </BannerLink>
      {menuOpen == false && (
        <BurgerButton more_top={more_top} onClick={() => setMenuOpen(true)}>
          ☰
        </BurgerButton>
      )}
      {/* Inline menu on larger screens */}
      <DocumentsMenu>
        <SectionHeader colors={colors}>Resource Download</SectionHeader>
        <ResourceList colors={colors}>
          {resources?.map((res, i) => {
            const isExternal = !res.isInternal && res.isExternal !== false;
            const disabled = Boolean(res.disabled);
            return (
              <ResourceItem key={i}>
                {res.title.startsWith("NEW!") && <span>NEW! </span>}
                <a
                  href={disabled ? undefined : res.url}
                  target={disabled || !isExternal ? undefined : "_blank"}
                  rel={
                    disabled || !isExternal ? undefined : "noopener noreferrer"
                  }
                  aria-disabled={disabled ? true : undefined}
                  onClick={(e) => {
                    if (disabled) {
                      e.preventDefault();
                      return;
                    }
                    if (res.isInternal) {
                      e.preventDefault();
                      navigate(res.url);
                    }
                  }}
                >
                  {res.title.replace("NEW! ", "")}
                </a>
              </ResourceItem>
            );
          })}
        </ResourceList>
      </DocumentsMenu>

      {/* Overlay and sidebar on phones */}
      <Overlay open={menuOpen} onClick={() => setMenuOpen(false)} />
      <Sidebar open={menuOpen}>
        <CloseButton onClick={() => setMenuOpen(false)}>×</CloseButton>
        <SidebarMenu>
          {/* Banner inside burger menu */}
          <a
            href={resolvedCurriculumLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMenuOpen(false)}
          >
            <SidebarBanner src={MyInfoBannerImage} alt="Curriculum Banner" />
          </a>

          {/* Resource Download */}
          <SectionHeader colors={colors}>Resource Download</SectionHeader>
          <ResourceList colors={colors}>
            {resources?.map((res, i) => {
              const isExternal = !res.isInternal && res.isExternal !== false;
              const disabled = Boolean(res.disabled);
              return (
                <ResourceItem key={i}>
                  {res.title.startsWith("NEW!") && <span>NEW! </span>}
                  <a
                    href={disabled ? undefined : res.url}
                    target={disabled || !isExternal ? undefined : "_blank"}
                    rel={
                      disabled || !isExternal
                        ? undefined
                        : "noopener noreferrer"
                    }
                    aria-disabled={disabled ? true : undefined}
                    onClick={(e) => {
                      if (disabled) {
                        e.preventDefault();
                        return;
                      }
                      if (res.isInternal) {
                        e.preventDefault();
                        setMenuOpen(false);
                        navigate(res.url);
                      }
                    }}
                  >
                    {res.title.replace("NEW! ", "")}
                  </a>
                </ResourceItem>
              );
            })}
          </ResourceList>

          {/* Student Resources inside sidebar */}
          <SectionHeader colors={colors}>STUDENT RESOURCES</SectionHeader>
          <StudentResourcesBody colors={colors}>
            Available now, student activity pages are accessible individually as
            front-facing student resources. They’re accessed just like all other
            ISAFE lessons—either via a teacher’s My Info page or on our website.
          </StudentResourcesBody>
        </SidebarMenu>
      </Sidebar>

      {/* Student Resources stand-alone on desktop/tablet */}
      <StudentResourcesSection>
        <SectionHeader colors={colors}>STUDENT RESOURCES</SectionHeader>
        <StudentResourcesBody colors={colors}>
          Available now, student activity pages are accessible individually as
          front-facing student resources. They’re accessed just like all other
          ISAFE lessons—either via a teacher’s My Info page or on our website.
        </StudentResourcesBody>
      </StudentResourcesSection>
    </DocumentsBlock>
  );
}
